import "./styles.scss";

import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import { useTasks } from "context/webmaster/tasks";
import { useTimeTracking } from "context/time-tracking";
import useResponsive from "libs/helpers/useResponsive";
import usePagination from "libs/helpers/usePagination";

import DataTile from "components/DataTile";
import TaskInfo from "components/TaskInfo";
import PillGridNew from "components/PillGridNew";
import Pagination from "components/Pagination";
import Button from "components/Button";
import { ModalFilters } from "./components";
import Select from "components/form/Select";
import ScrollTopMobile from "components/ScrollTopMobile";
import Loader from "components/Loader";

import { TASKS_PER_PAGE } from "libs/mobile_settings";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";
import { tasks_sorting } from "libs/sorting";

import { ReactComponent as NoDataIcon } from "assets/icons/no-data.svg";

const mainClass = "tasks-webmaster-tiles";

const TasksTiles = ({ setWebsiteInfoId, setExplanation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data,
    loading,
    updateTask,
    refetchTasks,
    filters,
    filterHandler,
    applyFilters,
    filterClearAll,
  } = useTasks();
  const { current, timeTracking } = useTimeTracking();
  const { max } = useResponsive();

  const perPage = max.sm ? TASKS_PER_PAGE.mobile : TASKS_PER_PAGE.default;
  const { currentPage, totalPages, handlePageChange, paginatedData } =
    usePagination(data, perPage);

  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  useEffect(() => {
    if (!max.sm && isMobileFiltersOpen) setIsMobileFiltersOpen(false);
  }, [max.sm, isMobileFiltersOpen]);

  useEffect(() => {
    if (max.sm) {
      const priority_filters = filters?.priority?.split(",");
      const status_filters = filters?.status?.split(",");

      const snapshot = {
        priority:
          priority_filters?.length > 0
            ? task_priority_data?.find(
                (item) => item?.key === priority_filters[0],
              ) || null
            : null,
        status:
          status_filters?.length > 0
            ? task_status_data?.find(
                (item) => item?.key === status_filters[0],
              ) || null
            : null,
        time_over: filters?.time_over === "true" ? true : false,
      };

      if (priority_filters?.length > 1 || status_filters?.length > 1)
        applyFilters(snapshot);
    }
  }, [filters, applyFilters, max.sm]);

  return (
    <>
      <div className={mainClass}>
        <div className={`${mainClass}__header`}>
          <div className={`${mainClass}__header-filters`}>
            {max.sm ? (
              <>
                <div>
                  <Button
                    variant="transparent"
                    onClick={() => setIsMobileFiltersOpen(true)}
                  >
                    {t("Filter results")}
                  </Button>
                </div>
                <div>
                  <Button
                    disabled={
                      Object.keys(filters).filter((item) => item !== "sort")
                        .length === 0
                    }
                    onClick={filterClearAll}
                  >
                    {t("Clear filters")}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <PillGridNew
                    header="Status"
                    label={t("Select status")}
                    selected={filters?.status}
                    options={task_status_data.map((item) => ({
                      ...item,
                      label: t(item.label),
                    }))}
                    optionsFooter={
                      <TaskInfo onClick={() => setExplanation("status")} />
                    }
                    onChange={(option) =>
                      filterHandler("status", option.key, true)
                    }
                  />
                </div>
                <div>
                  <PillGridNew
                    header={t("Priority")}
                    label={t("Select priority")}
                    selected={filters?.priority}
                    icon="flag"
                    options={task_priority_data.map((item) => ({
                      ...item,
                      label: t(item.label),
                    }))}
                    optionsFooter={
                      <TaskInfo
                        type="priority"
                        onClick={() => setExplanation("priority")}
                      />
                    }
                    onChange={(option) =>
                      filterHandler("priority", option.key, true)
                    }
                  />
                </div>
              </>
            )}
          </div>
          <div className={`${mainClass}__header-sort`}>
            <Select.Sort
              initial={filters?.sort}
              options={tasks_sorting}
              forceBottom
              onChange={(option) => filterHandler("sort", option.value)}
            />
          </div>
        </div>
        <AnimatePresence initial={false} mode="wait">
          <motion.div
            key={currentPage}
            className={`${mainClass}__row`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: "tween", duration: 0.3 }}
          >
            {!loading ? (
              paginatedData.length > 0 ? (
                paginatedData.map(
                  ({
                    _id,
                    created_at,
                    priority,
                    title,
                    website,
                    total_time,
                    due_date,
                    status,
                  }) => {
                    const status_data = task_status_data?.find(
                      (item) => item?.value === status,
                    );
                    const priority_data = task_priority_data?.find(
                      (item) => item?.value === priority,
                    );
                    const isCompletelyDisabled =
                      status_data?.key === "canceled";

                    return (
                      <DataTile
                        key={_id}
                        label={title}
                        status={{
                          data: {
                            value: status_data?.value,
                            label: t(status_data?.label),
                            color: status_data?.color,
                          },
                          options: !isCompletelyDisabled
                            ? task_status_data
                                .filter((item) => item?.key !== "canceled")
                                .map((item) => ({
                                  ...item,
                                  label: t(item.label),
                                }))
                            : [],
                          optionsFooter: !isCompletelyDisabled ? (
                            <TaskInfo
                              onClick={() => setExplanation("status")}
                            />
                          ) : null,
                          isCompletelyDisabled,
                          isCompact: true,
                          onChange: !isCompletelyDisabled
                            ? (option) =>
                                updateTask(_id, { status: option.value })
                            : undefined,
                        }}
                        priority={{
                          data: {
                            value: priority_data?.value,
                            label: t(priority_data?.label),
                            color: priority_data?.color,
                          },
                          icon: "flag",
                          options: !isCompletelyDisabled
                            ? task_priority_data.map((item) => ({
                                ...item,
                                label: t(item.label),
                              }))
                            : [],
                          optionsFooter: !isCompletelyDisabled ? (
                            <TaskInfo
                              type="priority"
                              onClick={() => setExplanation("priority")}
                            />
                          ) : null,
                          isCompletelyDisabled,
                          isCompact: true,
                          onChange: !isCompletelyDisabled
                            ? (option) =>
                                updateTask(_id, { priority: option.value })
                            : undefined,
                        }}
                        time_tracker={{
                          label: total_time,
                          start_time_tracked:
                            current?.tasks?.[0]?._id === _id
                              ? current?.start
                              : null,
                          disable_tracking: isCompletelyDisabled,
                          onclick: () => timeTracking(_id, refetchTasks),
                        }}
                        due_date={{
                          value: due_date,
                          isCompletelyDisabled,
                          onChange: (date) => {
                            updateTask(_id, { due_date: date });
                          },
                        }}
                        created_at={created_at}
                        website={{
                          name: website?.name,
                          onClick: () => setWebsiteInfoId(website?._id),
                        }}
                        onClick={() => {
                          const params = new URLSearchParams();
                          if (filters?.status)
                            params.set("status", filters.status);
                          if (filters?.priority)
                            params.set("priority", filters.priority);
                          if (filters?.sort) params.set("sort", filters.sort);
                          if (currentPage > 1) params.set("page", currentPage);
                          const query = params.toString();
                          navigate(`/tasks/${_id}${query ? `?${query}` : ""}`);
                        }}
                      />
                    );
                  },
                )
              ) : (
                <div className={`${mainClass}__no-data`}>
                  <NoDataIcon />
                  <p>{t("No data")}</p>
                </div>
              )
            ) : (
              <div className={`${mainClass}__loader`}>
                <Loader color="blue" center size="large" />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
        {totalPages > 1 && (
          <div className={`${mainClass}__pagination`}>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onChange={handlePageChange}
            />
          </div>
        )}
        <div className={`${mainClass}__scroll`}>
          <ScrollTopMobile />
        </div>
      </div>

      {max.sm && (
        <ModalFilters
          isOpen={isMobileFiltersOpen}
          onClose={() => setIsMobileFiltersOpen(false)}
        />
      )}
    </>
  );
};

export default TasksTiles;
